// src/mazot-takip/src/pages/Hareketler.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";

/* helpers */
const fmt = (n) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(
    Number(n || 0)
  );
const safeUpper = (s = "") => (s ? String(s).toUpperCase() : s);
const tsToDateStr = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    const p = (x) => String(x).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(
      d.getHours()
    )}:${p(d.getMinutes())}`;
  } catch {
    return "-";
  }
};

// aktif/çalışan personel kontrolü: giris sayısı > cikis sayısı
function isPersonActive(tarih = {}) {
  const keys = Object.keys(tarih || {});
  const giris = keys.filter((k) => k.toLowerCase().startsWith("giris")).length;
  const cikis = keys.filter((k) => k.toLowerCase().startsWith("cikis")).length;
  return giris > cikis;
}

export default function Hareketler() {
  const [mode, setMode] = useState("plate"); // 'plate' | 'driver'
  const [plates, setPlates] = useState([]); // {plate, model, aracCinsi, tesis}
  const [people, setPeople] = useState([]); // {id, ad, soyad, gorev, tesis, full}
  const [input, setInput] = useState(""); // seçilen plaka veya kişi adı
  const [selectedDriverId, setSelectedDriverId] = useState(null); // kişi seçilince id
  const [rows, setRows] = useState([]); // hareketler
  const [loading, setLoading] = useState(false);

  // listeler
  useEffect(() => {
    (async () => {
      const [plSnap, peSnap] = await Promise.all([
        getDocs(collection(db, "plates")),
        getDocs(collection(db, "personeller")),
      ]);

      // SADECE AKTİF PLAKALAR
      const pls = plSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((x) => x.active !== false)
        .map((x) => ({
          plate: safeUpper(x.plate),
          model: x.model || "",
          aracCinsi: x.aracCinsi || "",
          tesis: x.tesis || "",
        }))
        .filter((x) => x.plate)
        .sort((a, b) => a.plate.localeCompare(b.plate, "tr"));
      setPlates(pls);

      // SADECE ÇALIŞAN PERSONELLER (aktif & tarihine göre hâlâ çalışan)
      const pe = peSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((x) => x.active !== false && isPersonActive(x.tarih || {}))
        .map((x) => ({
          id: x.id,
          ad: x.ad || "",
          soyad: x.soyad || "",
          gorev: x.gorev || "",
          tesis: x.tesis || "",
          full: ((x.ad || "") + " " + (x.soyad || "")).trim(),
        }))
        .filter((x) => x.full)
        .sort((a, b) => a.full.localeCompare(b.full, "tr"));
      setPeople(pe);
    })();
  }, []);

  // datalist seçenekleri
  const plateOptions = useMemo(() => plates.map((p) => p.plate), [plates]);
  const peopleOptions = useMemo(() => people.map((p) => p.full), [people]);

  // kişi adı yazıldığında id eşle
  useEffect(() => {
    if (mode !== "driver") return;
    const f = people.find((x) => x.full === input);
    setSelectedDriverId(f ? f.id : null);
  }, [mode, input, people]);

  async function search() {
    if (!input.trim()) {
      alert(mode === "plate" ? "Bir plaka seçiniz." : "Bir personel seçiniz.");
      return;
    }
    setLoading(true);
    try {
      const col = collection(db, "fuel_transactions");
      let docs = [];

      if (mode === "plate") {
        const q1 = query(col, where("vehiclePlate", "==", safeUpper(input)));
        const s1 = await getDocs(q1);
        docs = s1.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      } else {
        if (selectedDriverId) {
          const q1 = query(col, where("driverId", "==", selectedDriverId));
          const s1 = await getDocs(q1);
          docs = s1.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        } else {
          const q1 = query(col, where("driverName", "==", input));
          const s1 = await getDocs(q1);
          docs = s1.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        }
      }

      // filtrele & sırala (client-side)
      const list = docs
        .filter((t) => t.deleted !== true)
        .sort((a, b) => {
          const ams =
            a.date?.toDate ? a.date.toDate().getTime() : a.date?.seconds * 1000 || 0;
          const bms =
            b.date?.toDate ? b.date.toDate().getTime() : b.date?.seconds * 1000 || 0;
          return bms - ams; // yeni → eski
        });

      setRows(list);
    } finally {
      setLoading(false);
    }
  }

  // özet (sadece basit toplamlara dayalı)
  const summary = useMemo(() => {
    if (rows.length === 0)
      return { totalLt: 0, firstKm: null, lastKm: null, kmDelta: null, l100: null };

    const totalLt = rows.reduce((acc, r) => acc + (Number(r.liters) || 0), 0);

    // km bilgisi olanlar
    const withKm = rows
      .map((r) => ({
        km: typeof r.odometerKm === "number" ? r.odometerKm : null,
        ms: r.date?.toDate ? r.date.toDate().getTime() : r.date?.seconds * 1000 || 0,
      }))
      .filter((x) => x.km != null)
      .sort((a, b) => a.ms - b.ms); // eski→yeni

    let firstKm = null,
      lastKm = null,
      kmDelta = null,
      l100 = null;
    if (withKm.length >= 1) {
      firstKm = withKm[0].km;
      lastKm = withKm[withKm.length - 1].km;
      kmDelta = lastKm > firstKm ? lastKm - firstKm : 0;
      l100 = kmDelta > 0 ? (totalLt / kmDelta) * 100 : null;
    }

    return { totalLt, firstKm, lastKm, kmDelta, l100 };
  }, [rows]);

  // seçime uygun datalist id/label
  const datalistId = mode === "plate" ? "dl-plates" : "dl-people";
  const labelText = mode === "plate" ? "Plaka" : "Personel";

  // kart başlığı için seçilenin ek bilgisi
  const selectedInfo = useMemo(() => {
    if (mode === "plate") {
      const p = plates.find((x) => x.plate === safeUpper(input));
      return p ? `(${p.tesis || "-"} • ${p.aracCinsi || "-"} • ${p.model || "-"})` : "";
    } else {
      const f = people.find((x) => x.full === input);
      return f ? `(${f.tesis || "-"} • ${f.gorev || "-"})` : "";
    }
  }, [mode, input, plates, people]);

  return (
    <div style={{ padding: 20, paddingTop: 88, maxWidth: 1200, margin: "0 auto" }}>
      <h2
        style={{
          textAlign: "left",
          color: "#334155",
          fontSize: 22,
          fontWeight: "bold",
          marginBottom: 12,
        }}
      >
        Hareketler
      </h2>

      {/* arama formu */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 12,
          alignItems: "end",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="radio"
              name="mode"
              value="plate"
              checked={mode === "plate"}
              onChange={() => {
                setMode("plate");
                setInput("");
                setRows([]);
              }}
            />
            Plaka
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="radio"
              name="mode"
              value="driver"
              checked={mode === "driver"}
              onChange={() => {
                setMode("driver");
                setInput("");
                setRows([]);
              }}
            />
            Personel
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
            {labelText}
          </label>
          <input
            list={datalistId}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === "plate" ? "Örn: 06ABC123" : "Örn: Ahmet Yılmaz"}
            style={{ border: "1px solid #cbd5e1", padding: 10, borderRadius: 8 }}
          />
          {mode === "plate" ? (
            <datalist id="dl-plates">
              {plateOptions.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          ) : (
            <datalist id="dl-people">
              {peopleOptions.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          )}
        </div>

        <div>
          <button
            onClick={search}
            style={{
              backgroundColor: "#4f46e5",
              color: "white",
              padding: "10px 16px",
              borderRadius: 8,
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Listele
          </button>
        </div>
      </div>

      {/* seçilen başlık */}
      {input.trim() && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: "#f1f5f9",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            color: "#0f172a",
            fontSize: 14,
          }}
        >
          <b>{mode === "plate" ? "Plaka" : "Personel"}:</b> {input}{" "}
          <span style={{ color: "#475569" }}>{selectedInfo}</span>
        </div>
      )}

      {/* özet */}
      {rows.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 12,
            fontSize: 13,
            color: "#334155",
          }}
        >
          <div>
            <b>Toplam Litre:</b> {fmt(summary.totalLt)} L
          </div>
          <div>
            <b>İlk KM:</b> {summary.firstKm ?? "-"}
          </div>
          <div>
            <b>Son KM:</b> {summary.lastKm ?? "-"}
          </div>
          <div>
            <b>KM Artışı:</b> {summary.kmDelta ?? "-"}
          </div>
          <div>
            <b>L/100km:</b> {summary.l100 != null ? fmt(summary.l100) : "-"}
          </div>
        </div>
      )}

      {/* tablo */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", minWidth: 1000 }}>
          <thead style={{ background: "#e2e8f0" }}>
            <tr>
              {[
                "Tarih",
                "Plaka",
                "Şoför",
                "Litre",
                "KM",
                "Kaynak",
                "Fiş No",
                "Tesis",
                "Giren",
              ].map((h) => (
                <th
                  key={h}
                  style={{ border: "1px solid #cbd5e1", padding: 8, textAlign: "left" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: 12, textAlign: "center" }}>
                  Yükleniyor...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 12, textAlign: "center", color: "#64748b" }}>
                  Kayıt yok.
                </td>
              </tr>
            ) : (
              rows.map((r) => (

                <tr key={r.id}>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {tsToDateStr(r.date)}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.vehiclePlate || "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.driverName || r.driverId || "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {fmt(r.liters)}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {typeof r.odometerKm === "number" ? fmt(r.odometerKm) : "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.sourceType === "tank" ? "Tank" : r.sourceType || "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.receiptNo || "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.tesis || "-"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: 8 }}>
                    {r.createdBy || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
        Not: Kayıtlar <b>onaylı & silinmemiş</b> dolumları içerir. Sıralama en yeni kayıttan başlar.
      </p>

      <style>{`
        @media print {
          button { display: none !important; }
          input { border: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
